/**
 * エリアごとの円グラフ (2026-09-18 オーナー要望)。
 *
 * 比較の表 (行 = 調査項目 / 列 = エリア) をエリア方向に転置し、1 エリア = 1 枚の円グラフにする。
 * 横棒の積み上げは「項目どうしの多さ」を見るのに向く一方、1 つのエリアの中で何がどれだけを
 * 占めるか (構成比) は読み取りにくいため、切り替えて見られるようにする。
 *
 * 追加のデータ取得は不要 (`buildAreaReportComparison` の結果をそのまま使える)。
 * 評価で分けるときは、項目のスライスをさらに ポジティブ → 中立 → ネガティブ に割る
 * (色は項目の色のまま、模様で区分を示す — 棒グラフと同じ規約)。
 */
import type { EvaluationPolarity } from '@citizen-walk/shared-types';
import type { AreaReportComparisonRow } from './areaReportComparison';
import { POLARITY_DISPLAY_ORDER } from './barFillStyles';

/** 円グラフの 1 切れ。 */
export interface DonutSlice {
  /** 項目のキー (評価で分けたときは `${key}__positive` 等)。 */
  key: string;
  /** 項目名 (評価で分けたときは「項目名（ポジティブ）」)。 */
  label: string;
  /** 項目の色 (評価で分けても項目の色は変えない)。 */
  color: string;
  value: number;
  /** 模様に使う評価の区分 (分けないときは null)。 */
  polarity: EvaluationPolarity | null;
}

/** 1 エリアぶんの円グラフ。 */
export interface AreaDonut {
  /** エリアの並び (選択順)。 */
  index: number;
  name: string;
  /** エリアの色 (地図・凡例と同じ)。 */
  color: string;
  pointCount: number;
  slices: DonutSlice[];
  /** 全スライスの合計 (0 なら「検出なし」表示)。 */
  total: number;
}

export interface BuildAreaDonutsInput {
  rows: AreaReportComparisonRow[];
  areas: { id: string; name: string; color: string; pointCount: number }[];
  /** 評価 (ポジ / 中立 / ネガ) でスライスを分けるか。 */
  splitByPolarity: boolean;
  /** 評価の区分の表示名 (地区の設定で変わる)。 */
  polarityLabels?: Record<EvaluationPolarity, string>;
  /** 件数そのものではなく 1 地点あたりにそろえるか (棒グラフと同じ既定)。 */
  perPoint?: boolean;
}

/**
 * 比較の行 (項目 × エリア) を、エリアごとの円グラフに組み替える。
 * 値が 0 の切れは作らない (凡例とツールチップが無駄に長くなるため)。
 */
export function buildAreaDonuts({
  rows,
  areas,
  splitByPolarity,
  polarityLabels,
  perPoint = true,
}: BuildAreaDonutsInput): AreaDonut[] {
  return areas.map((area, index) => {
    const slices: DonutSlice[] = [];
    for (const row of rows) {
      const total = (perPoint ? row.perPointValues[index] : row.values[index]) ?? 0;
      if (total <= 0) continue;
      const polarity = row.polarities[index] ?? null;
      if (!splitByPolarity || !polarity) {
        slices.push({
          key: row.key,
          label: row.label,
          color: row.color,
          value: total,
          polarity: null,
        });
        continue;
      }
      // 評価の内訳は保存時の件数ベースなので、1 地点あたり表示のときは同じ比率で割り当てる。
      const rawTotal = polarity.positive + polarity.neutral + polarity.negative;
      const scale = rawTotal > 0 ? total / rawTotal : 0;
      let assigned = 0;
      for (const k of POLARITY_DISPLAY_ORDER) {
        const value = polarity[k] * scale;
        if (value <= 0) continue;
        assigned += value;
        slices.push({
          key: `${row.key}__${k}`,
          label: `${row.label}（${polarityLabels?.[k] ?? k}）`,
          color: row.color,
          value,
          polarity: k,
        });
      }
      // 評価が付いていない残り (区分を解決できなかった検出)。
      const rest = total - assigned;
      if (rest > 0.0001) {
        slices.push({
          key: `${row.key}__none`,
          label: row.label,
          color: row.color,
          value: rest,
          polarity: null,
        });
      }
    }
    const total = slices.reduce((n, s) => n + s.value, 0);
    return {
      index,
      name: area.name,
      color: area.color,
      pointCount: area.pointCount,
      slices,
      total,
    };
  });
}

/** 円グラフの 1 切れの描画に必要な角度 (度、12 時から時計回り)。 */
export interface DonutArc extends DonutSlice {
  startAngle: number;
  endAngle: number;
  /** 構成比 (0〜1)。 */
  ratio: number;
}

/** スライスを角度に変換する (合計 0 のときは空配列)。 */
export function toDonutArcs(slices: DonutSlice[]): DonutArc[] {
  const total = slices.reduce((n, s) => n + s.value, 0);
  if (total <= 0) return [];
  let angle = 0;
  return slices.map((s) => {
    const ratio = s.value / total;
    const startAngle = angle;
    angle += ratio * 360;
    return { ...s, startAngle, endAngle: angle, ratio };
  });
}

/** 円弧のパス (SVG `d`)。中心 (cx, cy)、半径 r、12 時から時計回りの扇形。 */
export function donutArcPath(
  cx: number,
  cy: number,
  r: number,
  startAngle: number,
  endAngle: number,
): string {
  // 1 周ちょうどは扇形のパスにできない (始点と終点が同じ点になる) ので円として描く。
  if (endAngle - startAngle >= 359.999) {
    return `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx - 0.001} ${cy - r} Z`;
  }
  const point = (deg: number) => {
    const rad = ((deg - 90) * Math.PI) / 180;
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)] as const;
  };
  const [x1, y1] = point(startAngle);
  const [x2, y2] = point(endAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
}
